/**
 * Exit / Risk cluster — engine-validated cookbook seed recipes.
 *
 * Every entry is proven green by the companion test:
 *   buildGraphFromRecipe(entry.recipe).success === true
 *   validateGraph(nodes, edges).errors.length === 0
 *
 * Node-type and handle references verified against:
 *   apps/frontend/src/features/backtest/schemas/node-schemas/
 */

import type { CookbookEntry } from './types'

// ── 1. ATR Trailing Stop ─────────────────────────────────────
//
// Pattern: trail the stop at a dynamic multiple of ATR so it widens in
// volatile markets and tightens as volatility compresses.
//
// Nodes: priceData → averagetruerange(14) → multiply(×2) → trailingStop
const atrTrailingStop: CookbookEntry = {
  key: 'atr-trailing-stop',
  name: 'ATR Trailing Stop',
  category: 'exit-risk',
  intent: 'Trail the stop at 2× ATR — wider in volatile markets, tighter in calm ones.',
  summary:
    'Dynamic trailing stop that widens in volatile markets and tightens as volatility compresses.',
  difficulty: 'intermediate',
  whenToUse:
    'Use in trending markets where you want to ride momentum while protecting against a genuine reversal. Most effective when the asset has a consistent ATR reading and clear directional bias.',
  whatToLookFor:
    'Monitor whether the ATR is expanding (stop widens — trend has energy) or contracting (stop tightens — trend may be exhausting). A close below the trailing level signals invalidation.',
  howItWorks:
    'priceData feeds the full OHLCV stream to averagetruerange(14), which outputs the current ATR value. multiply(×2) scales that ATR by the desired multiplier — here 2× — producing the stop distance. trailingStop in Condition mode receives that scaled value each bar and ratchets the stop upward as price advances, never moving it back down.',
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      // ATR requires the full OHLCV stream
      id: 'atr',
      type: 'averagetruerange',
      params: { period: 14 },
      inputs: { input_1: 'price.ohlcv' },
    },
    {
      // Scale ATR by the desired multiplier (static input_2 via discriminatedUnion)
      id: 'atr_mult',
      type: 'multiply',
      inputs: { input_1: 'atr.atr', input_2: 2 },
    },
    {
      // Condition-mode trailing stop: the engine evaluates the scaled ATR each bar
      id: 'trail',
      type: 'trailingStop',
      params: { trailType: 'Condition' },
      inputs: { signal: 'atr_mult.value' },
    },
  ],
  unavailable:
    'The Trade Manager trails by a percent or ticks; it cannot trail an ATR distance yet.',
}

// ── 2. ATR Position Sizing ───────────────────────────────────
//
// Pattern: size the position so that a 1-ATR adverse move equals the
// configured risk amount. Larger ATR → fewer shares; calmer tape → more.
//
// The engine's Risk ($) sizer divides the risk by the distance to the
// strategy's stop, so the recipe sets that stop: close minus the scaled ATR,
// frozen at entry. (It used to feed the ATR to Position Sizing, which the
// engine ignores; the strategy then had no stop to size from.)
//
// Nodes: priceData → sma(1) + averagetruerange(14) → multiply(×1) → subtract → Trade Manager stop
const atrPositionSizing: CookbookEntry = {
  key: 'atr-position-sizing',
  name: 'ATR Position Sizing',
  category: 'exit-risk',
  intent: 'Size the position so that a 1-ATR move equals target risk.',
  summary: 'Volatility-scaled position sizing: larger ATR → fewer shares, calmer tape → more.',
  difficulty: 'intermediate',
  tags: ['ATR'],
  whenToUse:
    "Apply any time you want consistent dollar risk across trades regardless of the asset's volatility regime. Particularly useful when switching between instruments with very different ATR profiles.",
  whatToLookFor:
    'Check that the ATR reading is stable and representative of recent conditions — a spike from a recent news event can temporarily inflate ATR and produce an unusually small position size.',
  howItWorks:
    "priceData feeds OHLCV to averagetruerange(14), outputting a per-bar ATR value. multiply(×1) scales it to the invalidation distance (raise the multiplier to widen the stop), and subtract takes that distance off the close to give the stop price, which the Trade Manager's Price level stop fixes at entry. Sizing set to Risk ($) 100 then buys $100 ÷ (entry − stop) shares, so a wider ATR means fewer shares and a tighter ATR means more shares, keeping dollar risk constant. The stop sits below the close, for a long.",
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      id: 'atr',
      type: 'averagetruerange',
      params: { period: 14 },
      inputs: { input_1: 'price.ohlcv' },
    },
    {
      // Period-1 SMA is a numeric pass-through for close price (ohlcv → number)
      id: 'close_num',
      type: 'simplemovingaverage',
      params: { period: 1 },
      inputs: { input_1: 'price.close' },
    },
    {
      // Multiply ATR by a scalar to arrive at the invalidation distance in dollars
      id: 'atr_scaled',
      type: 'multiply',
      inputs: { input_1: 'atr.atr', input_2: 1 },
    },
    {
      // Stop price = close - scaled ATR (a long's stop)
      id: 'stop_price',
      type: 'subtract',
      inputs: { input_1: 'close_num.sma', input_2: 'atr_scaled.value' },
    },
  ],
  tradeManager: {
    params: { stopType: 'Static Value', sizingMethod: 'Risk ($)', sizingValue: 100 },
    feeds: { socket: 'stopLevel', from: 'stop_price.value' },
  },
}

// ── 4. Structure Stop (Session Low) ─────────────────────────
//
// Pattern: place the stop below the session low, treating that level as the
// structural invalidation point.
//
// Nodes: priceData → rollingMinimum(session) → Trade Manager stop (Price level)
const structureStop: CookbookEntry = {
  key: 'structure-stop',
  name: 'Structure Stop (Session Low)',
  category: 'exit-risk',
  intent: 'Stop below the rolling session low — exit if price breaks structure.',
  summary:
    "Places the stop at the session's rolling low, exiting only if structural support is broken.",
  difficulty: 'beginner',
  tags: ['Highs and lows'],
  whenToUse:
    'Best on intraday momentum setups where the session low represents a meaningful support level. Avoids the noise of ATR-based distances when there is a clear structural reference point.',
  whatToLookFor:
    'Watch for sessions with a well-defined, established low. If the session low keeps creeping down early in the day, the structural anchor is weak and this stop may be too loose.',
  howItWorks:
    "priceData provides the bar-level low stream. rollingMinimum in session mode tracks the lowest low seen so far in the current session, updating on every bar. The Trade Manager's Price level stop reads that rolling minimum at entry, so the stop sits at the session's low as of the entry bar and stays there for the trade.",
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      // Rolling session low of the 'low' field
      id: 'session_low',
      type: 'rollingMinimum',
      params: { reference: 'session' },
      inputs: { input_1: 'price.low' },
    },
  ],
  tradeManager: {
    params: { stopType: 'Static Value' },
    feeds: { socket: 'stopLevel', from: 'session_low.minimum' },
  },
}

// ── 5. Profit Target (ATR Multiple) ─────────────────────────
//
// Pattern: place a profit target at a fixed ATR multiple above entry.
// Entry close + N × ATR gives the target price level.
//
// Note: priceData only exports an ohlcv-typed output. To get a numeric close
// for the add node (which accepts only number), pipe close through a period-1
// SMA, a pass-through that outputs type 'number'.
//
// Nodes: priceData → sma(1,close)[number] + atr → multiply(×2) → add → Trade Manager target
const profitTargetAtr: CookbookEntry = {
  key: 'profit-target-atr',
  name: 'Profit Target (ATR Multiple)',
  category: 'exit-risk',
  intent:
    'Take profit at 2× ATR above entry close: a dynamic target that breathes with volatility.',
  summary:
    'Sets a take-profit at 2× ATR above the current close, keeping the target proportional to volatility.',
  difficulty: 'intermediate',
  tags: ['ATR'],
  whenToUse:
    'Use in trending or breakout setups where you want the target to expand when volatility is high and contract when the tape is calm, rather than using a fixed point target.',
  whatToLookFor:
    'Verify the current ATR is representative: a volatility spike can set an unrealistically wide target. Pair with a stop at 1× ATR below entry to maintain a 2:1 reward-to-risk ratio.',
  howItWorks:
    "Because priceData exports close as an ohlcv-typed stream (not a plain number), a period-1 SMA acts as a type pass-through, converting close to a numeric signal. averagetruerange(14) produces the ATR value, which multiply(×2) scales to the target distance. add combines the numeric close with that distance to produce the absolute target price, which the Trade Manager's Price level target fixes at entry.",
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      // Period-1 SMA is a numeric pass-through for close price (ohlcv → number)
      id: 'close_num',
      type: 'simplemovingaverage',
      params: { period: 1 },
      inputs: { input_1: 'price.close' },
    },
    {
      id: 'atr',
      type: 'averagetruerange',
      params: { period: 14 },
      inputs: { input_1: 'price.ohlcv' },
    },
    {
      // Target distance = 2 × ATR
      id: 'atr_dist',
      type: 'multiply',
      inputs: { input_1: 'atr.atr', input_2: 2 },
    },
    {
      // Target price = current close (as number) + ATR distance
      id: 'target_price',
      type: 'add',
      inputs: { input_1: 'close_num.sma', input_2: 'atr_dist.value' },
    },
  ],
  tradeManager: {
    params: { targetType: 'Static Value' },
    feeds: { socket: 'targetLevel', from: 'target_price.value' },
  },
}

// ── 6. Scale Out + Move Stop to Breakeven ───────────────────
//
// Pattern: partial exits — sell 50 % at target 1 (ATR × 1 above close),
// sell remaining 50 % at target 2 (ATR × 2 above close), then move stop to
// breakeven after target 1 is hit (handled by trailingStop with Breakeven
// activation).
//
// priceData only exports ohlcv (not number), so close is extracted via a
// period-1 SMA before feeding into the add nodes.
//
// Nodes:
//   priceData → sma(1) → add(close + 1×atr) → target(t1) ─┐
//             → sma(1) → add(close + 2×atr) → target(t2) ─┤→ multiTarget
//   Trade Manager: Target Multi Target, trailing % activating at Breakeven
const scaleOutBreakeven: CookbookEntry = {
  key: 'scale-out-breakeven',
  name: 'Scale Out + Breakeven Stop',
  category: 'exit-risk',
  intent: 'Exit 50 % at 1× ATR, 50 % at 2× ATR; trail activates at breakeven.',
  summary:
    'Two partial exits at 1× and 2× ATR lock in gains; a breakeven-activated trail protects the remainder.',
  difficulty: 'advanced',
  tags: ['ATR'],
  whenToUse:
    'Use in momentum setups where you want to secure partial profits early while giving a portion of the position room to extend. Particularly effective when the first ATR target aligns with a nearby resistance level.',
  whatToLookFor:
    'Confirm there is enough ATR-measured room between entry and the first target for the trade to be worth taking. If ATR is very small, both targets compress and the risk-reward may not justify the entry.',
  howItWorks:
    "Close is extracted via a period-1 SMA (ohlcv-to-number pass-through) so the add nodes can accept it. Two parallel branches compute target prices: close + 1×ATR for the first partial exit and close + 2×ATR for the second. Each feeds a target node in Static Value mode, and multiTarget combines them with a 50/50 split into the Trade Manager's Multi Target. The Trade Manager's trailing stop, a 2% trail activating at Breakeven, keeps a floor under the remaining position: the trail only starts once entry cost is recovered.",
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      // Numeric close for add nodes (ohlcv → number via period-1 SMA)
      id: 'close_num',
      type: 'simplemovingaverage',
      params: { period: 1 },
      inputs: { input_1: 'price.close' },
    },
    {
      id: 'atr',
      type: 'averagetruerange',
      params: { period: 14 },
      inputs: { input_1: 'price.ohlcv' },
    },
    // First partial exit: 1× ATR
    {
      id: 'dist1',
      type: 'multiply',
      inputs: { input_1: 'atr.atr', input_2: 1 },
    },
    {
      id: 'add1',
      type: 'add',
      inputs: { input_1: 'close_num.sma', input_2: 'dist1.value' },
    },
    {
      id: 'tgt1',
      type: 'target',
      params: { targetType: 'Static Value' },
      inputs: { signal: 'add1.value' },
    },
    // Second partial exit: 2× ATR
    {
      id: 'dist2',
      type: 'multiply',
      inputs: { input_1: 'atr.atr', input_2: 2 },
    },
    {
      id: 'add2',
      type: 'add',
      inputs: { input_1: 'close_num.sma', input_2: 'dist2.value' },
    },
    {
      id: 'tgt2',
      type: 'target',
      params: { targetType: 'Static Value' },
      inputs: { signal: 'add2.value' },
    },
    // Combine both targets — 50 / 50 split
    {
      id: 'multi_tgt',
      type: 'multiTarget',
      params: { target1Percent: 50, target2Percent: 50 },
      inputs: { target1: 'tgt1.target', target2: 'tgt2.target' },
    },
  ],
  tradeManager: {
    params: {
      targetType: 'Custom',
      enableTrailing: true,
      trailType: '% Trade',
      activationTrigger: 'Breakeven',
    },
    feeds: { socket: 'targetCustom', from: 'multi_tgt.target' },
  },
}

// ── Export ───────────────────────────────────────────────────

export const exitRiskSeedRecipes: CookbookEntry[] = [
  atrTrailingStop,
  atrPositionSizing,
  structureStop,
  profitTargetAtr,
  scaleOutBreakeven,
]
